import { useState, useEffect, useCallback , useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Calendar, CheckSquare, ChevronLeft, ChevronRight, ChevronDown, Check, Clock, LayoutGrid, List, ListChecks, Repeat } from 'lucide-react';
import { workplanAPI, activitiesAPI, tasksAPI } from '../utils/api';
import { PageHeader } from '../components/layout/UI';
import { useToast, apiError } from '../components/Toast';
import { WORK_STATUSES, statusStyle } from '../utils/workStatus';

const iso   = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
const parse = (s) => new Date(String(s).slice(0,10) + 'T00:00:00');
const shift = (s, n) => { const d = parse(s); d.setDate(d.getDate() + n); return iso(d); };
const daysBetween = (a, b) => Math.round((parse(b) - parse(a)) / 86400000);
const dayName   = (s) => parse(s).toLocaleDateString('en-GB', { weekday: 'long' });
const longDate  = (s) => parse(s).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });
const shortDate = (s) => parse(s).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });

// Mid-tone accents, chosen to hold contrast on both the light and dark themes.
const AMBER = '#d97706';
const RED   = '#ef4444';
const TEAL  = '#5CC3BC';
// A miss is not an error and not a success. Darker than Hold's amber so the
// two are never confused at a glance.
const MISSED = '#b45309';

const REPEAT_LABEL = { daily: 'every day', weekdays: 'every weekday', weekly: 'weekly' };

/**
 * The Eisenhower matrix, derived rather than asked for.
 *
 * Nothing new is stored: the two axes come from fields the activity already
 * has, so a quadrant can never disagree with the list beside it, and nobody
 * has to maintain a second set of flags that drifts out of date.
 *
 *   URGENT     it is due today, overdue, or was carried from an earlier day.
 *              Time has run out, whatever it is worth.
 *   IMPORTANT  its priority is High or Critical.
 *
 * A repeating duty is urgent by nature — its day is today and there is no
 * tomorrow for this occurrence — so it sits on the urgent side regardless.
 */
// Every activity starts at 'Medium' (phase43 set it as the column default and
// backfilled every existing row), so Medium carries no information: it means
// "nobody said". Only a priority someone actually RAISED marks importance,
// and an activity still at the default inherits its task's instead — a task's
// priority is the one people set deliberately.
const IMPORTANT_PRIORITIES = ['High', 'Critical'];

const effectivePriority = (it) =>
  IMPORTANT_PRIORITIES.includes(it.priority) ? it.priority
  : (it.priority && it.priority !== 'Medium') ? it.priority
  : (it.task_priority || it.priority || null);

const isImportant = (it) => IMPORTANT_PRIORITIES.includes(effectivePriority(it));

const isUrgent = (it, today) =>
  it.kind === 'recurring'
  || !!it.carried_from
  || !!it.is_overdue
  || (!!it.due_date && String(it.due_date).slice(0, 10) <= today);

const QUADRANTS = [
  { key:'do',        title:'Do First',  rule:'Urgent & important',     color:'#ef4444',
    hint:'Today, in this order.' },
  { key:'schedule',  title:'Schedule',  rule:'Important, not urgent',  color:'#2E539F',
    hint:'Give it a day before it becomes urgent.' },
  { key:'delegate',  title:'Delegate',  rule:'Urgent, not important',  color:'#FFC849',
    hint:'Someone else could carry this.' },
  { key:'eliminate', title:'Eliminate', rule:'Neither',                color:'#94a3b8',
    hint:'Worth asking whether it needs doing at all.' },
];

const quadrantOf = (it, today) => {
  const u = isUrgent(it, today), i = isImportant(it);
  return u && i ? 'do' : i ? 'schedule' : u ? 'delegate' : 'eliminate';
};

/** A week of slippage should not look the same as a day of it. */
const carriedStyle = (days) => days >= 3
  ? { color: RED,   bg: 'rgba(239,68,68,.12)' }
  : { color: AMBER, bg: 'rgba(217,119,6,.12)' };

/** Due dates are only useful if urgency is visible without reading. */
const dueStyle = (due, today) => {
  if (!due) return null;
  const d = String(due).slice(0, 10);
  if (d <  today) return { color: RED,   label: `overdue · ${shortDate(d)}` };
  if (d === today) return { color: AMBER, label: 'due today' };
  return { color: 'var(--text-secondary)', label: `due ${shortDate(d)}` };
};

/**
 * Activities sit under the task they belong to. Anything that has no parent
 * task — a task standing for itself, a repeating task, a free-text repeating
 * entry — is its own group. Without the `!it.task_id` test every one of those
 * collapsed into a single group headed "Untitled task".
 */
const groupByTask = (items) => {
  const groups = new Map();
  for (const it of items) {
    const standalone = it.kind === 'task' || !it.task_id;
    const key   = standalone ? `t:${it.id}` : `t:${it.task_id}`;
    const label = standalone ? it.title : (it.task_title || 'Untitled task');
    if (!groups.has(key)) groups.set(key, { key, label, project: it.project_name, items: [] });
    groups.get(key).items.push(it);
  }
  return [...groups.values()];
};

export default function TodoPage() {
  const toast = useToast();
  const navigate = useNavigate();
  const [date, setDate]       = useState(iso(new Date()));
  const todayRef              = useRef(iso(new Date()));
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [busy, setBusy]       = useState({});
  // Open/closed per section, remembered per browser. A section the user
  // collapsed should stay collapsed when they come back to the page.
  const [open, setOpen] = useState(() => {
    const base = { carried:true, repeating:true, today:true, upcoming:true,
                   in_progress:true, done:false, blocked:false, skipped:false };
    try { return { ...base, ...JSON.parse(localStorage.getItem('tms_todo_open') || '{}') }; }
    catch { return base; }
  });
  const toggle = (id) => setOpen(o => {
    const next = { ...o, [id]: !o[id] };
    try { localStorage.setItem('tms_todo_open', JSON.stringify(next)); } catch { /* private window */ }
    return next;
  });
  // List or Matrix. Remembered per browser only — a view preference is not
  // worth a round trip, and it must survive the page being thrown away.
  const [view, setView]       = useState(() => {
    try { return localStorage.getItem('tms_todo_view') === 'matrix' ? 'matrix' : 'list'; }
    catch { return 'list'; }
  });
  const chooseView = (v) => {
    setView(v);
    try { localStorage.setItem('tms_todo_view', v); } catch { /* private window */ }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await workplanAPI.getTodo({ date });
      setData(r.data);
      setError('');
    } catch (e) {
      setError(e.response?.data?.message || 'Could not load your to-do list');
    } finally { setLoading(false); }
  }, [date]);

  useEffect(() => { load(); }, [load]);

  /**
   * A to-do list left open overnight showed yesterday, and a list left open
   * while work was planned elsewhere showed the plan as it was when the tab
   * was opened. Two cheap guards, no polling:
   *
   *   focus      coming back to the tab re-reads the day
   *   rollover   a minute tick moves the page onto the new day at midnight,
   *              but only while it is showing today — never while the user is
   *              deliberately looking at another date.
   */
  useEffect(() => {
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    const tick = setInterval(() => {
      const now = iso(new Date());
      setDate(d => (d !== now && d === todayRef.current) ? (todayRef.current = now) : d);
    }, 60000);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
      clearInterval(tick);
    };
  }, [load]);

  /**
   * One day of a repeating duty. The activity itself stays open all year, so
   * this writes an occurrence and never the record's status.
   *
   * Three states, cycled by tapping: nothing → done → skipped → nothing.
   * Skipped is not the same as untouched — it says the day was considered and
   * deliberately passed over, which is what stops it counting as outstanding.
   */
  const nextOccurrence = (item) =>
    item.is_done_today ? 'skipped' : item.is_skipped_today ? null : 'done';

  const setOccurrence = async (item, status) => {
    setBusy(b => ({ ...b, [item.id]: true }));
    try {
      await workplanAPI.setOccurrence({
        activity_id: item.activity_id || undefined,
        entry_id:    item.activity_id ? undefined : item.entry_id,
        occurrence_date: date,
        status,
      });
      await load();
    } catch (e) {
      toast.error(apiError(e, 'Could not update that item'));
    } finally {
      setBusy(b => { const n = { ...b }; delete n[item.id]; return n; });
    }
  };

  const setStatus = async (item, status) => {
    setBusy(b => ({ ...b, [item.id]: true }));
    try {
      const api = item.kind === 'task' ? tasksAPI : activitiesAPI;
      await api.update(item.id, { status });
      await load();
    } catch (e) {
      toast.error(apiError(e, 'Could not update that item'));
    } finally {
      setBusy(b => { const n = { ...b }; delete n[item.id]; return n; });
    }
  };

  const isToday = date === iso(new Date());
  const EMPTY_COUNTS = { carried: 0, repeating: 0, repeating_done: 0, repeating_skipped: 0,
                         today: 0, upcoming: 0, in_progress_tasks: 0, blocked: 0,
                         done: 0, skipped: 0 };
  const c = data?.counts || EMPTY_COUNTS;
  // A skipped day has been ANSWERED — it is no longer asking for a decision —
  // but it was not done, so it never counts towards progress. Those are two
  // different questions and they get two different numbers.
  const repeatAnswered = (c.repeating_done || 0) + (c.repeating_skipped || 0);
  const repeatOpen = (c.repeating || 0) - repeatAnswered;
  const openCnt = c.today + c.carried + repeatOpen;
  // DONE, not settled: only work that actually happened.
  const doneAll  = c.done + (c.repeating_done || 0);
  const missed   = (c.skipped || 0) + (c.repeating_skipped || 0);
  // Upcoming and in-progress tasks are context, not today's work, so they
  // stay out of the day's progress — otherwise next week's items would make
  // a finished day look unfinished. Skipped work stays IN the denominator:
  // a missed day should pull the day's figure down, not vanish from it.
  const tracked = c.today + c.carried + c.done + (c.skipped || 0) + (c.repeating || 0);
  const pct     = tracked ? Math.round((doneAll / tracked) * 100) : 0;
  const nothing = tracked + c.blocked + c.upcoming + c.in_progress_tasks === 0;

  // Reordering is per task group. The order is saved as a flat sort_order,
  // so dragging inside one group never disturbs another.
  const dragItem = useRef(null);
  const [dragOverKey, setDragOverKey] = useState(null);

  const saveOrder = async (ids) => {
    try {
      await workplanAPI.reorder(ids);
    } catch (e) {
      toast.error(apiError(e, 'Could not save the new order.'));
      load();                      // put the list back the way it was
    }
  };

  /** Move `from` to `to` within one group and persist the whole group. */
  const reorderWithin = (items, fromId, toId) => {
    const ids = items.map(i => i.entry_id).filter(Boolean);
    const a = ids.indexOf(fromId), b = ids.indexOf(toId);
    if (a < 0 || b < 0 || a === b) return;
    ids.splice(b, 0, ids.splice(a, 1)[0]);
    // Show it immediately; the reload reconciles.
    setData(d => {
      if (!d) return d;
      const order = new Map(ids.map((id, i) => [id, i]));
      const sortGroup = (list) => [...(list || [])].sort((x, y) => {
        const ix = order.has(x.entry_id) ? order.get(x.entry_id) : Infinity;
        const iy = order.has(y.entry_id) ? order.get(y.entry_id) : Infinity;
        return ix - iy;
      });
      return { ...d, today: sortGroup(d.today), carried: sortGroup(d.carried) };
    });
    saveOrder(ids);
  };

  /**
   * One item, as a tile rather than a line.
   *
   * The number is the item's position in its section, so "third one down" is
   * something you can say out loud. The whole tile is the link — a 14px title
   * was a small target, and everything on the card belongs to the same record
   * — with the status control and the Start button stopping the click so they
   * do not navigate away mid-action.
   */
  /**
   * One item, as a tile on the page background.
   *
   * The left edge carries the section's colour, so a card tells you which
   * group it belongs to even when you have scrolled past the heading. The
   * whole tile is the link; the status control stops the click so choosing a
   * status does not also navigate away.
   */
  const Row = ({ item, dim, index, accent }) => {
    const repeating = item.kind === 'recurring';
    const skipped = repeating ? item.is_skipped_today : item.status === 'Skipped';
    const done    = repeating ? item.is_done_today    : item.status === 'Completed';
    const working = !!busy[item.id];
    const slipped = item.slipped_from ? daysBetween(item.slipped_from, date) : 0;
    const due     = dueStyle(item.due_date, date);
    const sty     = statusStyle(item.status);

    const circle = repeating
      ? { shape: '50%',
          border: done ? TEAL : skipped ? MISSED : 'var(--border)',
          fill:   done ? TEAL : 'transparent',
          label:  done ? "Today done — tap to skip instead"
                : skipped ? 'Today skipped — tap to clear'
                : "Mark today's occurrence done" }
      : { shape: '6px',
          border: done ? TEAL : skipped ? MISSED : 'var(--border)',
          fill:   done ? TEAL : 'transparent',
          label:  done ? 'Reopen' : 'Mark done' };

    const go = () => navigate(item.kind === 'task' ? `/tasks/${item.id}`
      : `/activities/${item.activity_id || item.id}`);

    // A repeating card answers for ITS DAY; everything else carries the
    // record's own status. Two different vocabularies, one control.
    const opts = repeating
      ? [{ v:'', l:'Not yet' }, { v:'done', l:'Done' }, { v:'skipped', l:'Skipped' }]
      : WORK_STATUSES.map(v => ({ v, l: v }));
    const current = repeating ? (item.occurrence_status || '') : item.status;

    return (
      <div
        role="button" tabIndex={0}
        onClick={go}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); go(); } }}
        className="tile flex items-center gap-3 px-3 py-2.5 cursor-pointer"
        style={{ borderLeftColor: accent || 'var(--border)', opacity: dim ? 0.8 : 1 }}>

        <span className="w-6 h-6 rounded-lg flex items-center justify-center text-[11px] font-semibold flex-shrink-0 select-none"
          style={{ background: 'var(--surface-alt)', color: 'var(--text-secondary)' }}>
          {index + 1}
        </span>

        <button
          onClick={e => { e.stopPropagation(); repeating
            ? setOccurrence(item, nextOccurrence(item))
            : setStatus(item, done ? 'In Progress' : 'Completed'); }}
          disabled={working}
          aria-label={circle.label} title={circle.label}
          className="w-[18px] h-[18px] flex items-center justify-center flex-shrink-0"
          style={{ borderRadius: circle.shape, border: `2px solid ${circle.border}`,
                   background: circle.fill, transition: 'background .15s ease, border-color .15s ease' }}>
          {done    && <Check size={11} color="#fff" strokeWidth={3.5} />}
          {skipped && <span style={{ width: 8, height: 2, background: MISSED, borderRadius: 1 }}/>}
        </button>

        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium leading-snug truncate"
            style={{ color: done ? 'var(--text-secondary)' : 'var(--text)' }}>
            <span className={done ? 'todo-struck' : ''}
              style={skipped ? { color: MISSED } : undefined}>{item.title}</span>
          </p>
          <div className="flex items-center gap-2 mt-0.5 flex-wrap text-[11px]"
            style={{ color: 'var(--text-secondary)' }}>
            {/* Project → Task, so a title on its own is never ambiguous. */}
            {(item.project_name || item.task_title) && (
              <span className="truncate" style={{ opacity: .85 }}>
                {[item.project_name, item.task_title].filter(Boolean).join(' → ')}
              </span>
            )}
            {due && <span className="flex items-center gap-1" style={{ color: due.color }}>
              <Clock size={10}/>{due.label}</span>}
            {item.planned_hours > 0 && <span>{parseFloat(item.planned_hours).toFixed(2)}h</span>}
            {repeating && <span className="flex items-center gap-1" style={{ color: TEAL }}>
              <Repeat size={10}/>{REPEAT_LABEL[item.recurrence] || 'repeats'}</span>}
            {skipped && <span style={{ color: MISSED }}>
              {repeating ? 'skipped today' : 'skipped — not done'}</span>}
            {item.scheduled_for && <span style={{ color:'#2E539F' }}>planned {dayName(item.scheduled_for)}</span>}
            {item.kind === 'task' && <span>standalone task</span>}
          </div>
        </div>

        {/* How late, in the least space it can be said. */}
        {item.slipped_from && !done && (
          <span className="text-[11px] px-1.5 py-0.5 rounded font-semibold flex-shrink-0"
            title={item.is_overdue
              ? `Due ${shortDate(item.slipped_from)} — ${slipped} day${slipped===1?'':'s'} overdue`
              : `Planned for ${dayName(item.slipped_from)} — carried ${slipped} day${slipped===1?'':'s'}`}
            style={{ background: carriedStyle(slipped).bg, color: carriedStyle(slipped).color }}>
            +{slipped}d
          </span>
        )}

        <select
          value={opts.some(o => o.v === current) ? current : opts[0].v}
          disabled={working}
          onClick={e => e.stopPropagation()}
          onChange={e => { e.stopPropagation();
            repeating ? setOccurrence(item, e.target.value || null)
                      : setStatus(item, e.target.value); }}
          className="text-xs rounded-lg px-2 py-1.5 border font-medium cursor-pointer outline-none flex-shrink-0"
          style={{ background:'var(--surface)',
                   borderColor: repeating ? 'var(--border)' : sty.border,
                   color:       repeating ? 'var(--text-secondary)' : sty.text }}>
          {opts.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
        </select>
      </div>
    );
  };

  // Tiles carry their own border, so the group only needs a quiet label above
  // them — no second box around the box.
  const TaskGroup = ({ group, dim, from = 0, accent }) => (
    <div className="mb-3 last:mb-0">
      {group.label && (
        <p className="text-[11px] font-semibold px-1 mb-1.5 truncate" style={{ color: 'var(--text-secondary)' }}>
          {group.label}
        </p>
      )}
      <div className="flex flex-col gap-2">
        {group.items.map((it, i) => (
          <div key={it.kind + it.id}
            draggable={!!it.entry_id}
            onDragStart={() => { dragItem.current = it.entry_id; }}
            onDragEnd={() => { dragItem.current = null; setDragOverKey(null); }}
            onDragOver={e => { if (it.entry_id && dragItem.current) { e.preventDefault(); setDragOverKey(it.entry_id); } }}
            onDrop={e => {
              e.preventDefault();
              if (dragItem.current && it.entry_id) reorderWithin(group.items, dragItem.current, it.entry_id);
              dragItem.current = null; setDragOverKey(null);
            }}
            style={{
              cursor: it.entry_id ? 'grab' : 'default',
              borderTop: dragOverKey === it.entry_id ? '2px solid #2E539F' : '2px solid transparent',
            }}>
            <Row item={it} dim={dim} index={from + i} accent={accent} />
          </div>
        ))}
      </div>
    </div>
  );

  /**
   * A section: a heading on the page background, tiles beneath it.
   *
   * Nothing is wrapped in an outer card. The background now carries enough
   * contrast to do that job on its own, and one box inside another box is how
   * a list of six items ends up looking like a filing cabinet.
   *
   * `always` keeps it on the page when empty, with a line saying so — the
   * five sections the day is organised around are a fixed shape, and hiding
   * one on a quiet day made the page look like the feature was missing rather
   * than the work. Done, Skipped and On hold are overflow and still disappear.
   *
   * Numbering runs CONTINUOUSLY down the section, across task groups, so the
   * numbers match what you count on screen rather than restarting per group.
   */
  const Section = ({ id, title, hint, accent, icon: Icon, items, always, empty, badge }) => {
    if (!items?.length && !always) return null;
    const isOpen = open[id] !== false;
    const groups = groupByTask(items || []);
    let running = 0;
    return (
      <div className="mb-5 last:mb-0">
        <button onClick={() => toggle(id)} className="section-head">
          {Icon && <Icon size={16} style={{ color: accent || 'var(--text-secondary)' }}/>}
          <h2 className="text-base font-semibold" style={{ color: 'var(--text)' }}>{title}</h2>
          <span className="text-xs px-2 py-0.5 rounded-full font-semibold"
            style={{ background: `${accent || '#64748b'}1f`, color: accent || 'var(--text-secondary)' }}>
            {badge || (items?.length || 0)}
          </span>
          <ChevronDown size={16} className="ml-auto" style={{
            color: 'var(--text-secondary)',
            transform: isOpen ? 'none' : 'rotate(-90deg)', transition: 'transform .15s ease' }}/>
        </button>
        {hint && isOpen && (
          <p className="text-xs px-1 pb-2" style={{ color: 'var(--text-secondary)' }}>{hint}</p>
        )}
        {isOpen && (
          items?.length
            ? groups.map(g => { const from = running; running += g.items.length;
                return <TaskGroup key={g.key} group={g} from={from} accent={accent}/>; })
            : <p className="px-1 py-2 text-sm" style={{ color:'var(--text-secondary)' }}>
                {empty || 'None'}
              </p>
        )}
      </div>
    );
  };

  /**
   * In-progress tasks are context, not a checklist. No tick, no Start button:
   * a task closes when its activities do, so offering to complete it here
   * would be a second, contradictory way to close it.
   */
  const TaskContext = ({ items, always }) => {
    if (!items?.length && !always) return null;
    const isOpen = open.in_progress !== false;
    return (
      <div className="card p-0 overflow-hidden" style={{ borderLeft: '3px solid #2E539F' }}>
        <button onClick={() => toggle('in_progress')}
          className="w-full px-4 py-3 flex items-baseline gap-2 flex-wrap text-left">
          <ChevronDown size={14} style={{ color:'var(--text-secondary)', alignSelf:'center',
            transform: isOpen ? 'none' : 'rotate(-90deg)', transition:'transform .15s ease' }}/>
          <h2 className="text-sm font-semibold" style={{ color: 'var(--text)' }}>In-progress tasks</h2>
          <span className="text-xs px-1.5 py-0.5 rounded font-semibold"
            style={{ background: 'rgba(46,83,159,.12)', color: '#2E539F' }}>{items.length}</span>
          <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>
            what the work above rolls up into
          </span>
        </button>
        {isOpen && (
        <div className="px-2 pb-2">
          {!items?.length && (
            <p className="px-3 py-2 text-sm" style={{ color:'var(--text-secondary)' }}>
              No task is in progress.
            </p>
          )}
          {(items || []).map(t => {
            const due = dueStyle(t.due_date, date);
            return (
              <div key={t.id} className="flex items-center gap-3 py-2 pl-3 pr-2">
                <div className="flex-1 min-w-0">
                  <button onClick={() => navigate(`/tasks/${t.id}`)}
                    className="text-left text-sm leading-snug block w-full truncate"
                    style={{ color: 'var(--text)' }}>{t.title}</button>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    {t.project_name && (
                      <span className="text-[11px]" style={{ color: 'var(--text-secondary)', opacity: .7 }}>
                        {t.project_name}
                      </span>
                    )}
                    {t.act_total > 0 && (
                      <span className="text-[11px]" style={{ color: 'var(--text-secondary)' }}>
                        {t.act_done}/{t.act_total} activities done
                      </span>
                    )}
                    {t.is_overdue
                      ? <span className="text-[11px] px-1.5 py-0.5 rounded font-medium"
                          style={{ background: 'rgba(239,68,68,.12)', color: RED }}>overdue</span>
                      : due && <span className="text-[11px]" style={{ color: due.color }}>{due.label}</span>}
                  </div>
                </div>
                {t.percent !== null && (
                  <div className="w-20 flex-shrink-0">
                    <div className="h-1 rounded-full overflow-hidden" style={{ background: 'var(--border)' }}>
                      <div className="h-full rounded-full"
                        style={{ width: `${t.percent}%`, background: '#2E539F' }}/>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        )}
      </div>
    );
  };

  /**
   * The matrix is a different ARRANGEMENT of the same items the list shows —
   * carried, repeating and today, which together are the day's actionable
   * work. Upcoming is deliberately out: it is not yet a call on today. So is
   * anything settled, which has no priority left to weigh.
   */
  const matrixItems = [...(data?.carried || []), ...(data?.repeating || []), ...(data?.today || [])];
  const byQuadrant = QUADRANTS.reduce((acc, q) => ({ ...acc, [q.key]: [] }), {});
  for (const it of matrixItems) byQuadrant[quadrantOf(it, date)].push(it);

  // Four empty boxes is a dead screen that explains nothing. Every activity
  // starts at Medium, so until someone raises one, the whole top row is empty
  // by construction — and that is worth saying out loud rather than leaving
  // the page to look broken.
  const anyImportant = matrixItems.some(isImportant);
  const anyUrgent    = matrixItems.some(it => isUrgent(it, date));

  const Quadrant = ({ q }) => {
    const items = byQuadrant[q.key];
    return (
      <div className="card p-0 overflow-hidden">
        <div className="px-4 pt-3 pb-2 flex items-baseline gap-2">
          <span className="w-2 h-2 rounded-full flex-shrink-0 self-center"
            style={{ background: q.color }}/>
          <div className="min-w-0">
            <p className="text-sm font-semibold" style={{ color: q.color }}>{q.title}</p>
            <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>{q.rule}</p>
          </div>
          <span className="ml-auto text-xs px-1.5 py-0.5 rounded font-semibold self-center"
            style={{ background: `${q.color}1f`, color: q.color }}>{items.length}</span>
        </div>
        <div className="px-2 pb-2">
          {items.length === 0
            ? <p className="px-3 py-2 text-sm" style={{ color: 'var(--text-secondary)' }}>None</p>
            : items.map((it, i) => (
                <div key={it.kind + it.id} className="mb-2 last:mb-0">
                  <Row item={it} index={i}/>
                  <p className="px-3 -mt-1 pb-1 text-[11px]" style={{ color:'var(--text-secondary)' }}>
                    priority {effectivePriority(it) || 'not set'}
                    {it.priority === 'Medium' && it.task_priority
                      && it.task_priority !== 'Medium' ? ' (from its task)' : ''}
                  </p>
                </div>
              ))}
        </div>
        {items.length > 0 && (
          <p className="px-4 pb-3 text-xs" style={{ color: 'var(--text-secondary)', opacity: .8 }}>
            {q.hint}
          </p>
        )}
      </div>
    );
  };

  // Collapsed was a second, nearly identical section shell. Section does the
  // same job now that every section collapses, so there is one of them.

  return (
    <div>
      {/* One piece of motion, on the single action that needs confirming. */}
      <style>{`
        @keyframes todoStrike { from { background-size: 0% 1px; } to { background-size: 100% 1px; } }
        .todo-struck {
          background-image: linear-gradient(currentColor, currentColor);
          background-repeat: no-repeat;
          background-position: 0 58%;
          animation: todoStrike .28s ease forwards;
        }
      `}</style>

      <PageHeader title="Daily To-Do" subtitle={longDate(date)}
        actions={
          <div className="inline-flex rounded-xl overflow-hidden border"
            style={{ borderColor:'var(--border)' }}>
            {[{ k:'list', label:'List', Icon:List }, { k:'matrix', label:'Matrix', Icon:LayoutGrid }]
              .map(({ k, label, Icon }) => (
              <button key={k} onClick={() => chooseView(k)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium transition-colors"
                style={view === k
                  ? { background:'var(--surface)', color:'var(--text)' }
                  : { background:'var(--surface-alt)', color:'var(--text-secondary)' }}>
                <Icon size={14}/> {label}
              </button>
            ))}
          </div>
        }/>

      {/* The error banner belongs here in the main return — `error` is out of
          scope inside Row, TaskGroup and Section. */}
      {error && (
        <div className="p-3 rounded-xl text-sm mb-4"
          style={{ background: 'rgba(239,68,68,.08)', color: RED, border: '1px solid rgba(239,68,68,.2)' }}>
          {error}
          <button className="ml-2 underline text-xs" onClick={() => setError('')}>Dismiss</button>
        </div>
      )}

      {/* Day progress: the figure is the point, so it is the biggest thing on
          the card and sits where the eye lands last. The date stepper lives
          beside it rather than in a second card. */}
      <div className="card mb-5">
        <div className="flex items-center gap-3 flex-wrap">
          <button onClick={() => setDate(d => shift(d, -1))}
            className="p-1.5 rounded-lg hover:bg-[var(--surface-alt)]" style={{ color: 'var(--text-secondary)' }}>
            <ChevronLeft size={16}/>
          </button>
          <div className="min-w-0">
            <p className="font-semibold" style={{ color: 'var(--text)' }}>
              {isToday ? 'Day progress' : dayName(date)}
            </p>
            <p className="text-xs mt-0.5" style={{ color: 'var(--text-secondary)' }}>
              {tracked === 0 ? 'nothing scheduled'
                : `${doneAll} of ${tracked} done${missed ? ` · ${missed} missed` : ''}`}
            </p>
          </div>
          <button onClick={() => setDate(d => shift(d, 1))}
            className="p-1.5 rounded-lg hover:bg-[var(--surface-alt)]" style={{ color: 'var(--text-secondary)' }}>
            <ChevronRight size={16}/>
          </button>
          {!isToday && (
            <button onClick={() => setDate(iso(new Date()))} className="btn-secondary text-xs">Today</button>
          )}
          <p className="ml-auto text-3xl font-bold"
            style={{ color: pct > 0 ? TEAL : 'var(--text-secondary)' }}>{pct}%</p>
        </div>
        <div className="mt-3 h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--surface-alt)' }}>
          <div className="h-full rounded-full"
            style={{ width: `${pct}%`, background: TEAL, transition: 'width .3s ease' }}/>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin"/>
        </div>
      ) : nothing ? (
        <div className="card text-center py-16">
          <ListChecks size={28} style={{ color: TEAL }} className="mx-auto mb-3"/>
          <p className="font-semibold" style={{ color: 'var(--text)' }}>Nothing assigned to you</p>
          <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
            Activities you create, or that someone assigns to you, appear here straight away.
          </p>
        </div>
      ) : view === 'matrix' ? (
        /* Same items, arranged by what they cost you rather than when they
           were planned. The quadrant is derived from the due date and the
           priority already on the record, so it cannot disagree with the
           list — and changing a priority moves the card without a second
           thing to keep up to date. */
        <div className="space-y-3">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {QUADRANTS.map(q => <Quadrant key={q.key} q={q}/>)}
          </div>
          <div className="px-1 space-y-1">
            <p className="text-xs" style={{ color:'var(--text-secondary)' }}>
              Urgent means due today, overdue, carried from an earlier day, or
              repeating today. Important means priority High or Critical, read
              from the activity, or from its task when the activity is still at
              the default.
            </p>
            {matrixItems.length > 0 && !anyImportant && (
              <p className="text-xs px-2 py-1.5 rounded"
                style={{ background:'rgba(255,200,73,.14)', color:'#92700f' }}>
                Nothing today is marked important, so the top row is empty.
                Every activity starts at Medium — raise one to High or Critical,
                on the activity or on its task, and it moves up here.
              </p>
            )}
            {matrixItems.length > 0 && !anyUrgent && (
              <p className="text-xs px-2 py-1.5 rounded"
                style={{ background:'rgba(46,83,159,.08)', color:'#2E539F' }}>
                Nothing today is urgent: none of it is overdue, due today, or
                carried from an earlier day.
              </p>
            )}
          </div>
          <TaskContext items={data.in_progress_tasks} always/>
        </div>
      ) : (
        <div className="space-y-3">
          {/* A quiet day is worth saying, but not by replacing the page: the
              five sections are a fixed shape, and swapping them for a single
              card made the rest look like it had gone missing. */}
          {openCnt === 0 && (
            <div className="card text-center py-10">
              <CheckSquare size={28} style={{ color: TEAL }} className="mx-auto mb-3"/>
              <p className="font-semibold" style={{ color: 'var(--text)' }}>
                {isToday ? "That's everything for today" : 'Nothing outstanding on this day'}
              </p>
              <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
                {c.done} finished{missed ? `, ${missed} missed` : ''}{c.blocked ? `, ${c.blocked} on hold` : ''}.
              </p>
            </div>
          )}
          <Section
            id="carried" title="Carried Forward" accent={AMBER} icon={AlertTriangle} items={data.carried} always
            empty="Nothing slipped from an earlier day."
            hint="planned or due on an earlier day — already missed once. Resolve or reschedule."/>

          <Section
            title={isToday ? 'Repeating today' : `Repeating on ${dayName(date)}`}
            id="repeating" accent={TEAL} icon={Repeat} items={data.repeating} always
            badge={`${c.repeating_done || 0}/${c.repeating || 0}`}
            empty="No recurring activity falls on this day. Set Repeats on an activity to put one here."
            hint="recurring duties. Tap the status circle to mark today's occurrence done or skipped — the activity continues tomorrow regardless."/>

          <Section
            id="today" title={isToday ? 'Today' : longDate(date)} accent="#ef4444" icon={Clock}
            items={data.today} always
            empty="Nothing planned or due for this day."/>

          <Section
            id="upcoming" title="Upcoming" accent="#2E539F" icon={Calendar} items={data.upcoming} always
            empty="Nothing planned or due in the next 7 days."
            hint="scheduled in the next 7 days — not due today, but worth knowing about."/>

          <TaskContext items={data.in_progress_tasks} always/>

          <Section id="done"    title="Done"    items={data.done}    accent={TEAL} icon={CheckSquare}/>
          <Section id="skipped" title="Skipped — not done" items={data.skipped} accent={MISSED} icon={AlertTriangle}/>
          <Section id="blocked" title="On hold" items={data.blocked} accent={AMBER} icon={AlertTriangle}/>
        </div>
      )}
    </div>
  );
}
